import { useState, useEffect } from 'react';

// Guapo character image
const GUAPO_IMAGE = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if mobile device
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Don't show if already installed
    if (standalone) {
      return;
    }

    // Check if user has dismissed the prompt recently (within 3 days)
    const dismissedAt = localStorage.getItem('install_prompt_dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 3) {
        return; // Don't show if dismissed within 3 days
      }
    }

    // For Android/Chrome - listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show prompt for ALL mobile users after a short delay
    if (mobile && !standalone) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 2000); // Show after 2 seconds
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Chrome install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('install_prompt_dismissed', Date.now().toString());
  };

  // Don't show if already installed or prompt shouldn't show
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4">
          <div className="flex items-center gap-3">
            <img 
              src={GUAPO_IMAGE} 
              alt="Guapo" 
              className="w-12 h-12 rounded-full border-2 border-white shadow-lg"
            />
            <div className="text-white">
              <h3 className="font-bold text-lg">Install GuapGoals!</h3>
              <p className="text-emerald-100 text-sm">Get the app experience 📱</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-3">
              <p className="text-gray-700 text-sm">
                Add GuapGoals to your home screen for quick access:
              </p>
              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</span>
                  <span>Tap the <strong>Share</strong> button</span>
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">2</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">3</span>
                  <span>Tap <strong>"Add"</strong> to confirm</span>
                </div>
              </div>
            </div>
          ) : deferredPrompt ? (
            // Android with install prompt available
            <div className="space-y-3">
              <p className="text-gray-700 text-sm">
                Install GuapGoals for a faster, app-like experience!
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-green-500">✓</span> Works offline
                <span className="text-green-500 ml-2">✓</span> Faster loading
                <span className="text-green-500 ml-2">✓</span> Home screen icon
              </div>
            </div>
          ) : (
            // Android without install prompt - show manual instructions
            <div className="space-y-3">
              <p className="text-gray-700 text-sm">
                Add GuapGoals to your home screen:
              </p>
              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</span>
                  <span>Tap the <strong>⋮ menu</strong> (3 dots)</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">2</span>
                  <span>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">3</span>
                  <span>Tap <strong>"Add"</strong> to confirm</span>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleDismiss}
              className="flex-1 py-2.5 px-4 text-gray-600 bg-gray-100 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Maybe Later
            </button>
            {deferredPrompt ? (
              <button
                onClick={handleInstall}
                className="flex-1 py-2.5 px-4 text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl font-bold hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg"
              >
                Install Now 🚀
              </button>
            ) : (
              <button
                onClick={handleDismiss}
                className="flex-1 py-2.5 px-4 text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl font-bold hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg"
              >
                Got It! 👍
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}

export default InstallPrompt;
